import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import { useAppSelector } from 'app/hooks';
import If from 'components/If';
import { finishAccountSetupWaitingPeriod } from 'utils/constants';
import {
  selectIsDataCollectionModalClosed,
  selectIsEligibleForDataCollection,
} from 'services/misc/miscSelectors';
import {
  selectIsExpiryNoticeModalClosed,
  selectIsEligibleForCtp,
  selectIsFinishAccountSetupModalClosed,
  selectIsEligibleForFinishAccountSetup,
} from 'services/ctp/ctpSelectors';
import { selectUserSince } from 'services/user/userSelectors';
import MembershipExpiryNotice from './MembershipExpiryNotice';
import MissingDataCollection from './MissingDataCollection';
import FinishAccountSetup from './FinishAccountSetup';

type sequenceProps = 'pii' | 'accountSetup' | 'ctp' | null;

function SessionModalSequence() {
  const [currentModal, setCurrentModal] = useState<sequenceProps>(null);
  const isEligibleForDataCollection = useAppSelector(
    selectIsEligibleForDataCollection,
  );

  const isDataCollectionModalClosed = useAppSelector(
    selectIsDataCollectionModalClosed,
  );
  const isEligibleForCtp = useAppSelector(selectIsEligibleForCtp);
  const isExpiryNoticeModalClosed = useAppSelector(
    selectIsExpiryNoticeModalClosed,
  );
  const isEligibleForFinishAccountSetup = useAppSelector(
    selectIsEligibleForFinishAccountSetup,
  );
  const isFinishAccountSetupModalClosed = useAppSelector(
    selectIsFinishAccountSetupModalClosed,
  );
  const userSince = useAppSelector(selectUserSince);

  /* Check is user eligible to collect the missing PII */
  const isDataCollectionEligible = useMemo(() => {
    return isEligibleForDataCollection && !isDataCollectionModalClosed;
  }, [isEligibleForDataCollection, isDataCollectionModalClosed]);

  /* Check is user eligible for CTP */
  const isMemberShipExpiryNoticeModalEligible = useMemo(() => {
    return isEligibleForCtp && !isExpiryNoticeModalClosed;
  }, [isEligibleForCtp, isExpiryNoticeModalClosed]);

  /* Check is user eligible for finish account setup */
  const isFinishAccountSetupModalEligible = useMemo(() => {
    const daysDiff = dayjs().diff(userSince, 'day');
    const showToday =
      daysDiff > finishAccountSetupWaitingPeriod &&
      (daysDiff === finishAccountSetupWaitingPeriod + 1 || daysDiff % 30 === 0);

    return (
      isEligibleForFinishAccountSetup &&
      showToday &&
      !isFinishAccountSetupModalClosed
    );
  }, [
    isEligibleForFinishAccountSetup,
    isFinishAccountSetupModalClosed,
    userSince,
  ]);

  const modalSequence = useMemo(() => {
    const list: sequenceProps[] = [];

    if (isDataCollectionEligible) {
      list.push('pii');
    }
    if (isFinishAccountSetupModalEligible) {
      list.push('accountSetup');
    }
    if (isMemberShipExpiryNoticeModalEligible) {
      list.push('ctp');
    }

    return list;
  }, [
    isDataCollectionEligible,
    isMemberShipExpiryNoticeModalEligible,
    isFinishAccountSetupModalEligible,
  ]);

  const handleNextModalSequence = () => {
    let modalName = null;
    if (modalSequence.length) {
      modalName = modalSequence.shift();
    }
    setCurrentModal(modalName ?? null);
  };

  useEffect(() => {
    if (
      modalSequence.length &&
      (!currentModal || modalSequence.includes(currentModal))
    ) {
      handleNextModalSequence();
    }
  }, [currentModal, modalSequence]);

  return (
    <>
      <If condition={currentModal === 'pii'}>
        <MissingDataCollection
          isOpen
          handleNextModalSequence={handleNextModalSequence}
        />
      </If>
      <If condition={currentModal === 'accountSetup'}>
        <FinishAccountSetup
          isOpen
          handleNextModalSequence={handleNextModalSequence}
        />
      </If>
      <If condition={currentModal === 'ctp'}>
        <MembershipExpiryNotice
          isOpen
          handleNextModalSequence={handleNextModalSequence}
        />
      </If>
    </>
  );
}

export default SessionModalSequence;
